import type { HTMLAttributes } from 'react';
import { Card } from '@/components/Card/Card';
import styles from './StatTile.module.css';

export interface StatTileBadge {
  label: string;
  tone: 'neutral' | 'action' | 'danger' | 'success';
}

export interface StatTileProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: string;
  value: string | number;
  subtitle: string;
  tone?: 'default' | 'action' | 'danger';
  empty?: boolean;
  badge?: StatTileBadge;
  detail?: string;
}

const TONE_CLASS: Record<NonNullable<StatTileProps['tone']>, string | undefined> = {
  default: undefined,
  action: styles.toneAction,
  danger: styles.toneDanger,
};

const BADGE_CLASS: Record<StatTileBadge['tone'], string> = {
  neutral: styles.badgeNeutral!,
  action: styles.badgeAction!,
  danger: styles.badgeDanger!,
  success: styles.badgeSuccess!,
};

export function StatTile({
  label,
  value,
  subtitle,
  tone = 'default',
  empty = false,
  badge,
  detail,
  className,
  ...rest
}: StatTileProps) {
  const valueClassName = [styles.value, TONE_CLASS[tone]].filter(Boolean).join(' ');
  return (
    <Card className={[styles.tile, className ?? ''].filter(Boolean).join(' ')} {...rest}>
      <div className={styles.header}>
        <p className={styles.label}>{label}</p>
        {badge && (
          <span className={[styles.badge, BADGE_CLASS[badge.tone]].join(' ')} data-role="badge">
            {badge.label}
          </span>
        )}
      </div>
      {empty ? (
        <div className={styles.emptyRule} data-testid="stat-tile-empty-rule" aria-hidden="true" />
      ) : (
        <p
          className={valueClassName}
          data-role="value"
          aria-label={typeof value === 'number' ? `${value} ${label.toLowerCase()}` : undefined}
        >
          {value}
        </p>
      )}
      <p className={styles.subtitle}>{subtitle}</p>
      {!empty && detail && (
        <>
          <hr className={styles.divider} />
          <p className={styles.detail} data-role="detail">
            {detail}
          </p>
        </>
      )}
    </Card>
  );
}
